<template>
  <v-menu
    data-testid="profile-dropdown"
    v-model="menuOpen"
    offset-y
    left
    :nudge-bottom="8"
    :close-on-content-click="false"
    content-class="profile-dropdown__menu"
    transition="fade-transition"
    :z-index="400"
    eager
  >
    <template #activator="{ on, attrs }">
      <button
        data-testid="profile-dropdown__activator"
        type="button"
        class="profile-dropdown__activator"
        v-bind="attrs"
        v-on="on"
        aria-label="Profile menu"
        aria-haspopup="true"
        :aria-expanded="String(menuOpen)"
      >
        <img
          v-if="user.profile && !avatarError"
          data-testid="profile-dropdown__avatar-img"
          :src="user.profile"
          alt="profile"
          class="profile-dropdown__avatar"
          @error="avatarError = true"
        />
        <span
          v-else
          data-testid="profile-dropdown__initials"
          class="profile-dropdown__initials"
        >{{ initials }}</span>
      </button>
    </template>

    <div data-testid="profile-dropdown__panel" class="profile-dropdown__panel">
      <div data-testid="profile-dropdown__header" class="profile-dropdown__header cursor-pointer" @click="onEditProfile"
      @keyup.enter="onEditProfile" role="button" tabindex="0">
        <div class="profile-dropdown__header-avatar">
          <img
            v-if="user.profile && !avatarError"
            data-testid="profile-dropdown__header-img"
            :src="user.profile"
            alt="profile"
            class="profile-dropdown__avatar"
            @error="avatarError = true"
          />
          <span
            v-else
            data-testid="profile-dropdown__header-initials"
            class="profile-dropdown__initials"
          >{{ initials }}</span>
        </div>
        <div class="profile-dropdown__header-text">
          <div data-testid="profile-dropdown__name" class="profile-dropdown__name">
            {{ user.name || 'User' }}
          </div>
          <div data-testid="profile-dropdown__email" class="profile-dropdown__email">
            {{ user.email || '' }}
          </div>
        </div>
      </div>

      <template v-if="showOrganisationSwitcher">
        <div class="profile-dropdown__divider" aria-hidden="true" />

        <profile-dropdown-organisation
          ref="organisation"
          :clients="clients"
          :selected-client-id="selectedClientId"
          @client-change="onClientChange"
        />
      </template>

      <div class="profile-dropdown__divider" aria-hidden="true" />

      <!-- <div data-testid="profile-dropdown__menu-section" class="profile-dropdown__menu-section">
        <div
          data-testid="profile-dropdown__edit-profile"
          class="profile-dropdown__action"
          role="button"
          tabindex="0"
          @click="onEditProfile"
          @keyup.enter="onEditProfile"
        >
          Profile settings
        </div>
        <div
          data-testid="profile-dropdown__support"
          class="profile-dropdown__action"
          role="button"
          tabindex="0"
          @click="onSupport"
          @keyup.enter="onSupport"
        >
          Help &amp; support
        </div>
      </div> -->

      <div class="profile-dropdown__divider" aria-hidden="true" />

      <div data-testid="profile-dropdown__signout-section" class="profile-dropdown__signout-section">
        <div
          data-testid="profile-dropdown__logout"
          class="profile-dropdown__action profile-dropdown__action--danger"
          role="button"
          tabindex="0"
          @click="onLogout"
          @keyup.enter="onLogout"
        >
          <img src="/img/navicon/fbc-nav/SignOutIcon.svg" alt="Logout Icon" />
          <span>Sign out</span>
        </div>
      </div>
    </div>
  </v-menu>
</template>

<script>
import { mapState } from 'vuex';
import { shouldShowOrganisationSwitcher } from '@/plugins/hostMode';
import ProfileDropdownOrganisation from './ProfileDropdownOrganisation.vue';

export default {
  name: 'ProfileDropdown',
  components: {
    ProfileDropdownOrganisation
  },
  props: {
    selectedClientId: { type: String, default: '' },
    clients: { type: Array, default: () => [] },
    fdSupportWidget: { type: Boolean, default: false }
  },
  emits: ['logout', 'edit-profile', 'client-change', 'support-click'],
  data() {
    return {
      menuOpen: false,
      avatarError: false
    };
  },
  computed: {
    ...mapState('auth', ['hostMode']),
    /** Dedicated hosts have a single bound client. UNKNOWN (L0) must keep the switcher. */
    showOrganisationSwitcher() {
      return shouldShowOrganisationSwitcher(this.hostMode);
    },
    user() {
      return this.$store?.state?.user || {};
    },
    initials() {
      // Figma avatar guidance: single initial to avoid overflow
      const name = (this.user.name || '').trim();
      return name ? name.charAt(0).toUpperCase() : 'U';
    }
  },
  methods: {
    close() {
      this.menuOpen = false;
    },
    onClientChange(clientId) {
      this.$emit('client-change', clientId);
      this.close();
    },
    onEditProfile() {
      this.$emit('edit-profile');
      this.close();
    },
    onSupport() {
      this.$emit('support-click');
      this.close();
    },
    onLogout() {
      this.$emit('logout');
      this.close();
    }
  },
  watch: {
    menuOpen(val) {
      if (!val) {
        this.avatarError = false;
        if (this._scrollSelectedTimer) {
          clearTimeout(this._scrollSelectedTimer);
          this._scrollSelectedTimer = null;
        }
        return;
      }
      // Menu content is teleported + animated — wait until it's measurable
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this._scrollSelectedTimer = setTimeout(() => {
            this.$refs.organisation?.scrollToSelected?.();
            this._scrollSelectedTimer = null;
          }, 60);
        });
      });
    }
  },
  beforeDestroy() {
    if (this._scrollSelectedTimer) {
      clearTimeout(this._scrollSelectedTimer);
      this._scrollSelectedTimer = null;
    }
    this.menuOpen = false;
    this.avatarError = false;
  }
};
</script>

<style lang="scss">
/* Unscoped: v-menu content is teleported outside the component root */
@import './ProfileDropdown.scss';
</style>
